import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, parseISO } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';

/**
 * Utility function to merge Tailwind CSS classes with clsx
 * Handles conditional classes and resolves conflicts
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as currency (USD by default)
 * @param amount - The amount to format
 * @param currency - The currency code (default: 'USD')
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a time string (HH:MM) for display in user's timezone
 * @param time - Time string in HH:MM format
 * @param timezone - User's timezone (optional, uses browser default)
 * @returns Formatted time string (e.g., "2:30 PM")
 */
export function formatTime(time: string, timezone?: string): string {
  // Parse time as today's date with the given time
  const today = new Date();
  const [hours, minutes] = time.split(':').map(Number);
  today.setHours(hours, minutes, 0, 0);

  if (timezone) {
    return formatInTimeZone(today, timezone, 'h:mm a');
  }

  return format(today, 'h:mm a');
}

/**
 * Format a date string for display
 * @param date - ISO date string or Date object
 * @param formatStr - Format string (default: 'MMM d, yyyy')
 * @param timezone - User's timezone (optional)
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date,
  formatStr: string = 'MMM d, yyyy',
  timezone?: string
): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (timezone) {
    const zonedDate = toZonedTime(dateObj, timezone);
    return format(zonedDate, formatStr);
  }

  return format(dateObj, formatStr);
}

/**
 * Format a date and time together
 * @param date - ISO date string
 * @param time - Time string in HH:MM format
 * @param timezone - User's timezone (optional)
 * @returns Formatted date and time string
 */
export function formatDateTime(date: string, time: string, timezone?: string): string {
  const dateTimeStr = `${date}T${time}:00`;
  const dateObj = parseISO(dateTimeStr);

  if (timezone) {
    return formatInTimeZone(dateObj, timezone, 'MMM d, yyyy h:mm a');
  }

  return format(dateObj, 'MMM d, yyyy h:mm a');
}

/**
 * Get initials from a name
 * @param firstName - First name
 * @param lastName - Last name (optional)
 * @returns Initials string (1-2 characters)
 */
export function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.charAt(0).toUpperCase() || '';
  const last = lastName?.charAt(0).toUpperCase() || '';
  return first + last || '?';
}

/**
 * Format a day of week for display
 * @param day - Day of week string
 * @returns Capitalized day name
 */
export function formatDayOfWeek(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1);
}

/**
 * Get short day name (Mon, Tue, etc.)
 * @param day - Day of week string
 * @returns Short day name
 */
export function getShortDayName(day: string): string {
  const dayMap: Record<string, string> = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };
  return dayMap[day.toLowerCase()] || day;
}

/**
 * Calculate duration between two time strings
 * @param startTime - Start time in HH:MM format
 * @param endTime - End time in HH:MM format
 * @returns Duration in hours (decimal)
 */
export function calculateDuration(startTime: string, endTime: string): number {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;

  return (endTotalMinutes - startTotalMinutes) / 60;
}

/**
 * Format duration in hours
 * @param hours - Duration in hours
 * @returns Formatted duration string
 */
export function formatDuration(hours: number): string {
  if (hours === 1) return '1 hour';
  if (hours === Math.floor(hours)) return `${hours} hours`;
  return `${hours.toFixed(1)} hours`;
}

/**
 * Debounce function
 * @param fn - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}
